/**
 * The select tool's own controls (SRS C4), shown while it is in hand.
 *
 * How to choose work, and then what can be done to it. The buttons stay visible with
 * nothing selected but say plainly that they are waiting, rather than appearing and
 * disappearing under the teacher's hand mid-lesson.
 */

import { ColorPalette } from './ColorPalette.js';
import { inkColor } from '../../board/palette.js';
import type { SelectMode } from '../../board/selection.js';
import { useBoardStore } from '../../board/store.js';

interface ModeChoice {
  readonly value: SelectMode;
  readonly label: string;
  readonly hint: string;
}

const MODES: readonly ModeChoice[] = [
  { value: 'lasso', label: 'Lasso', hint: 'Draw a loop around the work' },
  { value: 'rect', label: 'Box', hint: 'Drag a box over the work' },
];

function ModePicker() {
  const mode = useBoardStore((state) => state.selectMode);
  const setMode = useBoardStore((state) => state.setSelectMode);

  return (
    <div className="flex gap-2">
      {MODES.map(({ value, label, hint }) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={active}
            title={hint}
            onClick={() => {
              setMode(value);
            }}
            className={`min-h-touch flex-1 rounded-md border px-2 text-sm transition-colors duration-fast ${
              active
                ? 'border-primary bg-primary-subtle text-primary'
                : 'border-border bg-surface text-text-primary hover:bg-surface-hover'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

interface ActionProps {
  readonly label: string;
  readonly onClick: () => void;
  readonly disabled: boolean;
}

function Action({ label, onClick, disabled }: ActionProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="min-h-touch flex-1 rounded-md border border-border bg-surface px-2 text-sm text-text-primary transition-colors duration-fast hover:bg-surface-hover disabled:cursor-not-allowed disabled:text-text-secondary disabled:opacity-50"
    >
      {label}
    </button>
  );
}

/** How many strokes are selected, said the way a person would say it. */
function countLabel(count: number): string {
  if (count === 0) return 'Nothing selected yet';
  return count === 1 ? '1 thing selected' : `${count} things selected`;
}

/**
 * docs/06-UI-SPEC.md §16 — copy, cut and paste, with buttons as well as keys.
 *
 * The keyboard is the fast way, but a teacher on an iPad has no Ctrl key, and SRS A5
 * makes the tablet a first-class device. So the same three things are here too.
 */
function Clipboard() {
  const selection = useBoardStore((state) => state.selection);
  const count = useBoardStore((state) => state.selectionCount);
  const copied = useBoardStore((state) => state.selectionCopied);
  const empty = count === 0 || selection === null;

  return (
    <div className="flex gap-2">
      <Action
        label="Copy"
        disabled={empty}
        onClick={() => {
          selection?.copy();
        }}
      />
      <Action
        label="Cut"
        disabled={empty}
        onClick={() => {
          selection?.cut();
        }}
      />
      <Action
        label="Paste"
        disabled={copied === 0 || selection === null}
        onClick={() => {
          selection?.paste();
        }}
      />
    </div>
  );
}

/** What can be done to whatever is chosen. */
function Actions() {
  const selection = useBoardStore((state) => state.selection);
  const count = useBoardStore((state) => state.selectionCount);
  const grouped = useBoardStore((state) => state.selectionGrouped);
  const empty = count === 0 || selection === null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text-secondary">{countLabel(count)}</h3>
      <Clipboard />
      <div className="flex gap-2">
        <Action
          label="Duplicate"
          disabled={empty}
          onClick={() => {
            selection?.duplicate();
          }}
        />
        <Action
          label="Delete"
          disabled={empty}
          onClick={() => {
            selection?.remove();
          }}
        />
      </div>
      <div className="flex gap-2">
        <Action
          label={grouped ? 'Ungroup' : 'Group'}
          disabled={grouped ? empty : count < 2 || selection === null}
          onClick={() => {
            if (grouped) selection?.ungroup();
            else selection?.group();
          }}
        />
      </div>
      <p className="text-xs text-text-secondary">
        Grouped work moves as one for the rest of the lesson. Grouping is not saved with the
        lecture.
      </p>
      <p className="text-xs text-text-secondary">
        Press ? at any time for the full list of keyboard shortcuts.
      </p>
    </section>
  );
}

/** SRS C8 — sending shapes, words and notes forward and back over one another. */
function Stacking() {
  const selection = useBoardStore((state) => state.selection);
  const count = useBoardStore((state) => state.selectionCount);
  const empty = count === 0 || selection === null;

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text-secondary">Order</h3>
      <div className="flex gap-2">
        <Action
          label="To front"
          disabled={empty}
          onClick={() => {
            selection?.reorder('front');
          }}
        />
        <Action
          label="Forward"
          disabled={empty}
          onClick={() => {
            selection?.reorder('forward');
          }}
        />
      </div>
      <div className="flex gap-2">
        <Action
          label="Backward"
          disabled={empty}
          onClick={() => {
            selection?.reorder('backward');
          }}
        />
        <Action
          label="To back"
          disabled={empty}
          onClick={() => {
            selection?.reorder('back');
          }}
        />
      </div>
      <p className="text-xs text-text-secondary">
        Moves shapes, words and notes over and under one another. Pen and highlighter lines keep the
        order they were drawn in.
      </p>
    </section>
  );
}

export function SelectControls() {
  const selection = useBoardStore((state) => state.selection);
  const setColor = useBoardStore((state) => state.setColor);

  return (
    <>
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-text-secondary">How to choose</h3>
        <ModePicker />
        <p className="text-xs text-text-secondary">
          Drag over your work, then drag it to move it. The corners resize it and the circle above
          turns it.
        </p>
      </section>

      <Actions />
      <Stacking />

      <ColorPalette
        onPick={(choice) => {
          // The palette still sets the pen, so the next line drawn matches what the
          // teacher just chose here.
          setColor(choice);
          selection?.setColor(inkColor(choice));
        }}
      />
    </>
  );
}
